import { ArrowUpRight, Clock3, Radio, Users } from "lucide-react";
import type { Snapshot } from "../shared/contracts";
import {
  summarizeSnapshot,
  teamCapacity,
  monitoredAgents,
  type DispatchFilter,
} from "./dashboard-data";

export function DashboardOverview({
  snapshot,
  now,
  stale,
  activeFilter = "",
  teamFilter = "",
  onActivity,
  onTeam,
  onDueCallbacks,
}: {
  snapshot: Snapshot;
  now: number;
  stale: boolean;
  activeFilter?: string;
  teamFilter?: string;
  onActivity: (status: DispatchFilter | "") => void;
  onTeam?: (id: string) => void;
  onDueCallbacks: () => void;
}) {
  const summary = summarizeSnapshot(snapshot, now, stale);
  const capacity = teamCapacity(snapshot, now, stale);
  const states: {
    filter: DispatchFilter;
    count: number;
    label: string;
    detail: string;
  }[] = [
    {
      filter: "busy",
      count: summary.agents.busy,
      label: "Call session",
      detail: "Includes ringing",
    },
    {
      filter: "wrap_up",
      count: summary.agents.wrapUp,
      label: "Wrap-up",
      detail: "Finishing in GHL",
    },
    {
      filter: "reserved",
      count: summary.agents.reserved,
      label: "Reserved",
      detail: "Handoff in progress",
    },
    {
      filter: "away",
      count: summary.agents.away,
      label: "Away",
      detail: "Break, lunch or training",
    },
    {
      filter: "unknown",
      count: summary.agents.unknown,
      label: "Unknown",
      detail: "Check observation",
    },
    {
      filter: "offline",
      count: summary.agents.offline,
      label: "Offline",
      detail: "No fresh signal",
    },
  ];
  return (
    <section
      className="dispatch-overview"
      aria-label="Supervisor dispatch snapshot"
    >
      <div className="dispatch-board">
        <div className="dispatch-board-heading">
          <span className="dispatch-live">
            <Radio size={14} aria-hidden="true" />
            {stale ? "LAST RECEIVED SNAPSHOT" : "LIVE DISPATCH"}
          </span>
          <small>
            {snapshot.environment === "demo" ? "Fictional demo · " : ""}Snapshot{" "}
            {new Date(snapshot.serverTime).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            })}
          </small>
        </div>
        {stale ? (
          <div className="dispatch-unavailable" role="status">
            <strong>Activity unknown while disconnected</strong>
            <p>
              Live capacity and handoff actions return after GHL Connect
              reconnects. The roster below shows the last received people.
            </p>
          </div>
        ) : (
          <div className="dispatch-capacity">
            <button
              type="button"
              className={`capacity-main ${activeFilter === "ready" ? "capacity-selected" : ""}`}
              onClick={() =>
                onActivity(activeFilter === "ready" ? "" : "ready")
              }
              aria-pressed={activeFilter === "ready"}
            >
              <strong>
                {summary.agents.ready.toString().padStart(2, "0")}
              </strong>
              <span>
                <b>Ready for transfer</b>
                <small>Fresh idle signal · ready recipient</small>
              </span>
              <ArrowUpRight size={23} aria-hidden="true" />
            </button>
            <div className="capacity-context">
              <span>
                <Users size={16} aria-hidden="true" />
                <b>{monitoredAgents(snapshot.agents).length}</b> monitored
                people in your scope
              </span>
              <button
                type="button"
                onClick={() =>
                  onActivity(activeFilter === "available" ? "" : "available")
                }
                aria-pressed={activeFilter === "available"}
              >
                {summary.agents.available} available in monitored roster{" "}
                <ArrowUpRight size={14} aria-hidden="true" />
              </button>
              <small>
                Ready capacity includes eligible monitored agents in your
                scope. Request buttons exclude yourself.
              </small>
            </div>
          </div>
        )}
      </div>
      {!stale && (
        <div
          className="dispatch-states"
          aria-label="Filter roster by current activity"
        >
          {states.map(({ filter, count, label, detail }) => (
            <button
              type="button"
              key={filter}
              className={`dispatch-stat stat-${filter} ${activeFilter === filter ? "stat-selected" : ""}`}
              aria-pressed={activeFilter === filter}
              onClick={() => onActivity(activeFilter === filter ? "" : filter)}
            >
              <span className="dispatch-stat-count">
                {count}
                <ArrowUpRight size={14} aria-hidden="true" />
              </span>
              <strong>{label}</strong>
              <small>{detail}</small>
            </button>
          ))}
        </div>
      )}
      {capacity.length > 0 && (
        <div className="team-capacity" aria-label="Authorized team capacity">
          <div className="team-capacity-heading">
            <span>TEAM CAPACITY</span>
            <small>People in multiple teams appear in each.</small>
          </div>
          <div className="team-capacity-list">
            {capacity.map(({ team, total, ready, busy, unknownOffline }) => (
              <button
                type="button"
                key={team.id}
                className={
                  teamFilter === team.id ? "team-capacity-selected" : ""
                }
                aria-pressed={teamFilter === team.id}
                onClick={() => onTeam?.(teamFilter === team.id ? "" : team.id)}
                disabled={!onTeam}
              >
                <span className="capacity-team-name">
                  {team.name}
                  <small>
                    {total} monitored{!team.pilotEnabled ? " · pilot off" : ""}
                  </small>
                </span>
                <span className="capacity-team-values">
                  {stale ? (
                    <small>Activity unknown</small>
                  ) : (
                    <>
                      <b>
                        {ready}
                        <small> ready</small>
                      </b>
                      <span>{busy} call session</span>
                      {unknownOffline > 0 && (
                        <small>{unknownOffline} unknown / offline</small>
                      )}
                    </>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="dispatch-coordination">
        <div className="handoff-metrics">
          {snapshot.handoffMetrics ? (
            <>
              <span className="eyebrow">HANDOFF REQUESTS · LAST 24 HOURS</span>
              <strong>{snapshot.handoffMetrics.total} requests</strong>
              <small>
                Current outcomes: {snapshot.handoffMetrics.counts.pending}{" "}
                pending · {snapshot.handoffMetrics.counts.accepted} accepted ·{" "}
                {snapshot.handoffMetrics.counts.declined} declined ·{" "}
                {snapshot.handoffMetrics.counts.cancelled} cancelled ·{" "}
                {snapshot.handoffMetrics.counts.expired} expired ·{" "}
                {snapshot.handoffMetrics.counts.completed} completed
              </small>
              <small>
                GHL Connect coordination; these are not phone call transfers.
                {stale ? " Last received snapshot." : ""}
              </small>
            </>
          ) : (
            <p>
              Handoffs in {stale ? "last received" : "loaded"} snapshot:{" "}
              <strong>
                {summary.handoffs.pending} pending · {summary.handoffs.accepted}{" "}
                accepted
              </strong>
              <small>
                {summary.handoffs.expired} expired ·{" "}
                {summary.handoffs.completed} completed. GHL Connect coordination
                records, not phone call transfers.
              </small>
            </p>
          )}
        </div>
        <button type="button" className="secondary" onClick={onDueCallbacks}>
          <Clock3 size={15} aria-hidden="true" />
          {summary.dueCallbacks} due callback
          {summary.dueCallbacks === 1 ? "" : "s"}
          <ArrowUpRight size={14} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
